import { useEffect, useState } from 'react';
import { Page, Content } from '@backstage/core-components';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { Grid, Typography, Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { GettingStartedCard } from './GettingStartedCard';
import { MyServicesCard } from './MyServicesCard';
import { QuickLaunchCard } from './QuickLaunchCard';
import { RecentActivityCard } from './RecentActivityCard';
import { PlatformHealthCard } from './PlatformHealthCard';
import { PolicyComplianceCard } from './PolicyComplianceCard';
import { ActiveAgentsCard } from './ActiveAgentsCard';
import { StatCardsRow } from './StatCardsRow';
import { usePlatformLeadAccess } from './usePlatformLeadAccess';

const useStyles = makeStyles(theme => ({
  hero: {
    padding: theme.spacing(3, 0),
  },
  heroTitle: {
    fontSize: '2rem',
    fontWeight: 800,
    color: theme.palette.text.primary,
    letterSpacing: '-0.01em',
  },
  heroSubtitle: {
    fontSize: '0.9rem',
    color: theme.palette.text.secondary,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    marginTop: theme.spacing(0.5),
  },
  section: {
    marginTop: theme.spacing(6),
  },
  sectionLabel: {
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    fontSize: '0.7rem',
    fontWeight: 600,
    marginBottom: theme.spacing(2),
  },
}));

export const HomePage = () => {
  const classes = useStyles();
  const identityApi = useApi(identityApiRef);
  const isPlatformLead = usePlatformLeadAccess();
  const [userEntityRef, setUserEntityRef] = useState<string | undefined>();

  useEffect(() => {
    identityApi.getBackstageIdentity().then(id => {
      setUserEntityRef(id.userEntityRef);
    }).catch(() => {});
  }, [identityApi]);

  return (
    <Page themeId="home">
      <Content>
        {/* ── Hero Banner ───────────────────────────────────────────── */}
        <Box className={classes.hero}>
          <Typography className={classes.heroTitle}>Kyndryl IDP</Typography>
          <Typography className={classes.heroSubtitle}>
            Build · Deploy · Observe
          </Typography>
        </Box>

        {/* ── Stat Cards ────────────────────────────────────────────── */}
        <StatCardsRow />

        {/* ── Your Workspace ────────────────────────────────────────── */}
        <div className={classes.section}>
          <Typography className={classes.sectionLabel}>
            Your Workspace
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={7}>
              <MyServicesCard userEntityRef={userEntityRef} />
            </Grid>
            <Grid item xs={12} md={5}>
              <QuickLaunchCard />
            </Grid>
            <Grid item xs={12} md={8}>
              <RecentActivityCard userEntityRef={userEntityRef} />
            </Grid>
            <Grid item xs={12} md={4}>
              <GettingStartedCard userEntityRef={userEntityRef} />
            </Grid>
          </Grid>
        </div>

        {/* ── Platform Operations ───────────────────────────────────── */}
        {isPlatformLead && (
          <div className={classes.section}>
            <Typography className={classes.sectionLabel}>
              Platform Operations
            </Typography>
            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <PlatformHealthCard />
              </Grid>
              <Grid item xs={12} md={4}>
                <ActiveAgentsCard />
              </Grid>
              <Grid item xs={12} md={4}>
                <PolicyComplianceCard />
              </Grid>
            </Grid>
          </div>
        )}
      </Content>
    </Page>
  );
};
